import { useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ImportFromOtherPlatforms from "@/components/ImportFromOtherPlatforms";

/**
 * All data-backup UI in one place — lives inside the Settings page.
 *
 * - Native JSON export/import for round-tripping this user's own library
 *   between preview & production (chunked to duck the ingress body limit)
 * - Cross-platform importers (Tavern V2 card, Character.AI ZIP, free-form paste)
 *
 * Kept self-contained so Settings can compose it as a single Section child.
 */
export default function BackupSettings() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);

  async function exportData() {
    setExporting(true);
    try {
      const { data } = await api.get("/user/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      a.href = url;
      a.download = `immersive-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const totalChats = (data.chats || []).length;
      const totalMsgs = (data.chats || []).reduce((s, c) => s + (c.messages?.length || 0), 0);
      toast.success(`Backup downloaded — ${totalChats} chats, ${totalMsgs} messages.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed.");
    } finally {
      setExporting(false);
    }
  }

  async function onImportPick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.endsWith(".json")) {
      toast.error("Please choose a .json backup file.");
      return;
    }
    setImporting(true);
    try {
      const text = await file.text();
      let bundle;
      try { bundle = JSON.parse(text); }
      catch { throw new Error("File isn't valid JSON."); }
      if (bundle.version !== 1) throw new Error("Unsupported backup version.");
      const nChats = (bundle.chats || []).length;
      const nMsgs = (bundle.chats || []).reduce((s, c) => s + (c.messages?.length || 0), 0);
      const nChars = (bundle.characters || []).length;
      const ok = window.confirm(
        `Import this backup?\n\n` +
        `  • ${nChars} characters\n` +
        `  • ${nChats} chats\n` +
        `  • ${nMsgs} messages\n` +
        `  • from ${bundle.user_email || "(unknown)"}\n\n` +
        `Existing chats aren't touched. Duplicates are skipped automatically.` +
        (nChats > 10 ? `\n\nLarge backups are uploaded in chunks — this may take a minute.` : "")
      );
      if (!ok) return;

      // Chunked upload: ingress caps bodies around 1-2 MB. Split into a
      // small meta payload (chars + personas, no chats) then N chat batches
      // ≤ ~600 KB each. Backend `skip_duplicates` makes it idempotent.
      const totals = { characters: 0, personas: 0, chats: 0, messages: 0, skipped_chats: 0 };
      const merge = (s) => { for (const k of Object.keys(totals)) totals[k] += (s?.[k] || 0); };

      const metaBundle = { ...bundle, chats: [] };
      const { data: metaRes } = await api.post("/user/import", { bundle: metaBundle, strategy: "skip_duplicates" });
      merge(metaRes.stats);

      const chats = bundle.chats || [];
      const CHUNK_BYTES = 600_000;
      let batchStart = 0;
      while (batchStart < chats.length) {
        let batchEnd = batchStart;
        let running = 0;
        while (batchEnd < chats.length) {
          const cost = JSON.stringify(chats[batchEnd]).length;
          if (batchEnd > batchStart && running + cost > CHUNK_BYTES) break;
          running += cost;
          batchEnd += 1;
        }
        const chunkBundle = {
          version: bundle.version,
          exported_at: bundle.exported_at,
          user_email: bundle.user_email,
          characters: [],
          personas: [],
          chats: chats.slice(batchStart, batchEnd),
        };
        const { data: chunkRes } = await api.post("/user/import", { bundle: chunkBundle, strategy: "skip_duplicates" });
        merge(chunkRes.stats);
        batchStart = batchEnd;
        toast.info(
          `Importing… ${totals.chats}/${nChats} chats, ${totals.messages}/${nMsgs} messages so far.`,
          { duration: 1500 },
        );
      }

      toast.success(
        `Import complete — added ${totals.chats} chats, ${totals.messages} messages, ${totals.characters} characters.` +
        (totals.skipped_chats ? ` Skipped ${totals.skipped_chats} duplicates.` : "")
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Import failed.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="backup-settings">
      {/* Native JSON backup */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="profile-backup-section">
        <div className="mb-2 text-[10px] uppercase tracking-widest text-neutral-500">Backup &amp; Restore</div>
        <p className="text-sm text-neutral-300">
          Download a JSON backup of every character, persona, and chat you own — or restore from an earlier backup.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            data-testid="profile-export-btn"
            onClick={exportData}
            disabled={exporting}
            className="btn-pill bg-violet-500/15 border border-violet-500/30 text-violet-100 hover:bg-violet-500/25 disabled:opacity-40"
          >
            <Download size={14} /> {exporting ? "Preparing…" : "Download backup"}
          </button>
          <button
            data-testid="profile-import-btn"
            onClick={() => importFileRef.current?.click()}
            disabled={importing}
            className="btn-pill btn-ghost disabled:opacity-40"
          >
            <Upload size={14} /> {importing ? "Importing…" : "Import from backup"}
          </button>
          <input
            ref={importFileRef}
            type="file"
            accept="application/json,.json"
            onChange={onImportPick}
            className="hidden"
            data-testid="profile-import-file-input"
          />
        </div>
        <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
          Duplicates are skipped automatically — importing the same backup twice won't create copies.
        </p>
      </div>

      {/* Cross-platform character imports (Tavern / c.ai / paste) */}
      <ImportFromOtherPlatforms />
    </div>
  );
}
